/**
 * Purchase Order Service
 * TASK-007: Implements PO management and receiving per CONTEXT.md Section 2.2
 *
 * Features:
 * - Full PO lifecycle management
 * - Receiving workflow with discrepancy tracking
 * - Automatic supplier metric updates
 * - Short-shipping and late arrival detection
 */

import { supabase } from './supabase';
import { logErrorWithTrace } from './api';
import type {
    PurchaseOrder,
    PurchaseOrderSummary,
    OverduePurchaseOrder,
    POLineItem,
    ReceivingLog,
    ReceivingLineItem,
    POStatus,
    ReceivingType,
    ItemCondition,
    POCreateInput,
    POLineItemInput,
    ReceivingInput,
    ReceivingLineItemInput,
    DateRange,
    PaginatedResult,
} from '../types';

// ============================================
// TYPES
// ============================================

export interface POPaginationParams {
    page?: number;
    pageSize?: number;
    sortBy?: 'po_number' | 'order_date' | 'expected_arrival' | 'status' | 'total_order_value';
    sortOrder?: 'asc' | 'desc';
}

export interface POFilterParams {
    status?: POStatus | POStatus[];
    supplierId?: string;
    storeId?: string;
    isLate?: boolean;
    isShortShipped?: boolean;
    requiresReview?: boolean;
    dateRange?: DateRange;
}

export interface POUpdateInput {
    status?: POStatus;
    expectedArrival?: string;
    referenceNumber?: string;
    shippingMethod?: string;
    trackingNumber?: string;
    receivingNotes?: string;
    metadata?: Record<string, unknown>;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Map database row to PurchaseOrder type
 */
function mapDbToPurchaseOrder(row: any): PurchaseOrder {
    return {
        id: row.id,
        tenantId: row.tenant_id,
        poNumber: row.po_number,
        supplierId: row.supplier_id,
        storeId: row.store_id,
        status: row.status || 'draft',
        orderDate: row.order_date,
        expectedArrival: row.expected_arrival,
        actualArrival: row.actual_arrival,
        delayHours: row.delay_hours,
        isLate: row.is_late ?? false,
        totalUnitsOrdered: row.total_units_ordered ?? 0,
        totalUnitsReceived: row.total_units_received ?? 0,
        totalUnitsShort: row.total_units_short ?? 0,
        totalOrderValue: row.total_order_value ?? 0,
        totalReceivedValue: row.total_received_value ?? 0,
        receivedBy: row.received_by,
        receivingNotes: row.receiving_notes,
        isShortShipped: row.is_short_shipped ?? false,
        shortShipPercentage: row.short_ship_percentage,
        requiresReview: row.requires_review ?? false,
        reviewReason: row.review_reason,
        referenceNumber: row.reference_number,
        shippingMethod: row.shipping_method,
        trackingNumber: row.tracking_number,
        metadata: row.metadata || {},
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        createdBy: row.created_by,
    };
}

/**
 * Map database row to POLineItem type
 */
function mapDbToLineItem(row: any): POLineItem {
    return {
        id: row.id,
        purchaseOrderId: row.purchase_order_id,
        productId: row.product_id,
        lineNumber: row.line_number,
        quantityOrdered: row.quantity_ordered ?? 0,
        quantityReceived: row.quantity_received ?? 0,
        quantityDamaged: row.quantity_damaged ?? 0,
        quantityReturned: row.quantity_returned ?? 0,
        quantityAccepted: row.quantity_accepted ?? 0,
        quantityShort: row.quantity_short ?? 0,
        quantityOver: row.quantity_over ?? 0,
        unitCost: row.unit_cost ?? 0,
        lineTotal: row.line_total ?? 0,
        receivedValue: row.received_value ?? 0,
        discrepancyReason: row.discrepancy_reason,
        discrepancyNotes: row.discrepancy_notes,
        lotNumber: row.lot_number,
        expiryDate: row.expiry_date,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
    };
}

/**
 * Map database row to ReceivingLog type
 */
function mapDbToReceivingLog(row: any): ReceivingLog {
    return {
        id: row.id,
        tenantId: row.tenant_id,
        purchaseOrderId: row.purchase_order_id,
        receivedBy: row.received_by,
        receivedAt: row.received_at,
        receivingType: row.receiving_type || 'full',
        totalUnitsReceived: row.total_units_received ?? 0,
        totalUnitsDamaged: row.total_units_damaged ?? 0,
        totalValue: row.total_value,
        notes: row.notes,
        photoUrls: row.photo_urls || [],
        documentUrls: row.document_urls || [],
        qualityCheckPassed: row.quality_check_passed,
        qualityIssues: row.quality_issues || [],
        metadata: row.metadata || {},
        createdAt: row.created_at,
    };
}

// ============================================
// PURCHASE ORDER SERVICE
// ============================================

export const purchaseOrderService = {
    // ============================================
    // PURCHASE ORDER CRUD
    // ============================================

    /**
     * Get paginated list of purchase orders
     */
    async getPurchaseOrders(
        tenantId: string,
        pagination?: POPaginationParams,
        filters?: POFilterParams
    ): Promise<PaginatedResult<PurchaseOrder>> {
        if (!tenantId) {
            console.warn('[purchaseOrderService.getPurchaseOrders] tenantId is required');
            return { data: [], total: 0, page: 1, pageSize: 20, totalPages: 0 };
        }
        try {
            const page = pagination?.page ?? 1;
            const pageSize = pagination?.pageSize ?? 20;
            const sortBy = pagination?.sortBy ?? 'order_date';
            const sortOrder = pagination?.sortOrder ?? 'desc';
            const offset = (page - 1) * pageSize;

            let query = supabase
                .from('purchase_orders')
                .select('*', { count: 'exact' })
                .eq('tenant_id', tenantId);

            // Apply filters
            if (filters?.status) {
                const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
                query = query.in('status', statuses);
            }

            if (filters?.supplierId) {
                query = query.eq('supplier_id', filters.supplierId);
            }

            if (filters?.storeId) {
                query = query.eq('store_id', filters.storeId);
            }

            if (filters?.isLate !== undefined) {
                query = query.eq('is_late', filters.isLate);
            }

            if (filters?.isShortShipped !== undefined) {
                query = query.eq('is_short_shipped', filters.isShortShipped);
            }

            if (filters?.requiresReview !== undefined) {
                query = query.eq('requires_review', filters.requiresReview);
            }

            if (filters?.dateRange) {
                query = query
                    .gte('order_date', filters.dateRange.start)
                    .lte('order_date', filters.dateRange.end);
            }

            // Apply sorting and pagination
            query = query
                .order(sortBy, { ascending: sortOrder === 'asc' })
                .range(offset, offset + pageSize - 1);

            const { data, error, count } = await query;

            if (error) throw error;

            return {
                data: (data || []).map(mapDbToPurchaseOrder),
                total: count ?? 0,
                page,
                pageSize,
                totalPages: Math.ceil((count ?? 0) / pageSize),
            };
        } catch (error) {
            logErrorWithTrace('purchaseOrderService.getPurchaseOrders', error);
            throw error;
        }
    },

    /**
     * Get purchase order summaries with supplier info
     */
    async getPurchaseOrderSummaries(
        tenantId: string,
        pagination?: POPaginationParams,
        filters?: POFilterParams
    ): Promise<PaginatedResult<PurchaseOrderSummary>> {
        if (!tenantId) {
            console.warn('[purchaseOrderService.getPurchaseOrderSummaries] tenantId is required');
            return { data: [], total: 0, page: 1, pageSize: 20, totalPages: 0 };
        }
        try {
            const page = pagination?.page ?? 1;
            const pageSize = pagination?.pageSize ?? 20;
            const offset = (page - 1) * pageSize;

            // Use the purchase_order_summary view
            let query = supabase
                .from('purchase_order_summary')
                .select('*', { count: 'exact' })
                .eq('tenant_id', tenantId);

            if (filters?.status) {
                const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
                query = query.in('status', statuses);
            }

            if (filters?.supplierId) {
                query = query.eq('supplier_id', filters.supplierId);
            }

            query = query
                .order('order_date', { ascending: false })
                .range(offset, offset + pageSize - 1);

            const { data, error, count } = await query;

            if (error) throw error;

            return {
                data: (data || []).map(row => ({
                    ...mapDbToPurchaseOrder(row),
                    supplierCode: row.supplier_code,
                    supplierName: row.supplier_name,
                    supplierGrade: row.supplier_grade,
                    storeName: row.store_name,
                    lineItemCount: row.line_item_count ?? 0,
                })),
                total: count ?? 0,
                page,
                pageSize,
                totalPages: Math.ceil((count ?? 0) / pageSize),
            };
        } catch (error) {
            logErrorWithTrace('purchaseOrderService.getPurchaseOrderSummaries', error);
            throw error;
        }
    },

    /**
     * Get a single purchase order by ID
     */
    async getPurchaseOrder(id: string): Promise<PurchaseOrder | null> {
        try {
            const { data, error } = await supabase
                .from('purchase_orders')
                .select('*')
                .eq('id', id)
                .single();

            if (error) {
                if (error.code === 'PGRST116') return null;
                throw error;
            }

            return data ? mapDbToPurchaseOrder(data) : null;
        } catch (error) {
            logErrorWithTrace('purchaseOrderService.getPurchaseOrder', error);
            throw error;
        }
    },

    /**
     * Get purchase order by PO number
     */
    async getPurchaseOrderByNumber(poNumber: string): Promise<PurchaseOrder | null> {
        try {
            const { data, error } = await supabase
                .from('purchase_orders')
                .select('*')
                .eq('po_number', poNumber)
                .single();

            if (error) {
                if (error.code === 'PGRST116') return null;
                throw error;
            }

            return data ? mapDbToPurchaseOrder(data) : null;
        } catch (error) {
            logErrorWithTrace('purchaseOrderService.getPurchaseOrderByNumber', error);
            throw error;
        }
    },

    /**
     * Create a new purchase order with line items
     */
    async createPurchaseOrder(input: POCreateInput, tenantId: string): Promise<PurchaseOrder> {
        try {
            // Calculate totals from line items
            const totalUnitsOrdered = input.lineItems.reduce((sum, item) => sum + item.quantityOrdered, 0);
            const totalOrderValue = input.lineItems.reduce(
                (sum, item) => sum + item.quantityOrdered * item.unitCost,
                0
            );

            // Create the PO
            const { data: po, error: poError } = await supabase
                .from('purchase_orders')
                .insert({
                    tenant_id: tenantId,
                    po_number: input.poNumber,
                    supplier_id: input.supplierId,
                    store_id: input.storeId,
                    status: 'draft',
                    expected_arrival: input.expectedArrival,
                    reference_number: input.referenceNumber,
                    shipping_method: input.shippingMethod,
                    total_units_ordered: totalUnitsOrdered,
                    total_order_value: totalOrderValue,
                    metadata: input.metadata || {},
                })
                .select()
                .single();

            if (poError) throw poError;

            // Create line items
            const lineItems = input.lineItems.map((item, index) => ({
                purchase_order_id: po.id,
                product_id: item.productId,
                line_number: index + 1,
                quantity_ordered: item.quantityOrdered,
                unit_cost: item.unitCost,
                lot_number: item.lotNumber,
                expiry_date: item.expiryDate,
            }));

            const { error: lineError } = await supabase
                .from('po_line_items')
                .insert(lineItems);

            if (lineError) throw lineError;

            return mapDbToPurchaseOrder(po);
        } catch (error) {
            logErrorWithTrace('purchaseOrderService.createPurchaseOrder', error);
            throw error;
        }
    },

    /**
     * Update a purchase order
     */
    async updatePurchaseOrder(id: string, input: POUpdateInput): Promise<PurchaseOrder> {
        try {
            const updateData: Record<string, unknown> = {};

            if (input.status !== undefined) updateData.status = input.status;
            if (input.expectedArrival !== undefined) updateData.expected_arrival = input.expectedArrival;
            if (input.referenceNumber !== undefined) updateData.reference_number = input.referenceNumber;
            if (input.shippingMethod !== undefined) updateData.shipping_method = input.shippingMethod;
            if (input.trackingNumber !== undefined) updateData.tracking_number = input.trackingNumber;
            if (input.receivingNotes !== undefined) updateData.receiving_notes = input.receivingNotes;
            if (input.metadata !== undefined) updateData.metadata = input.metadata;

            const { data, error } = await supabase
                .from('purchase_orders')
                .update(updateData)
                .eq('id', id)
                .select()
                .single();

            if (error) throw error;

            return mapDbToPurchaseOrder(data);
        } catch (error) {
            logErrorWithTrace('purchaseOrderService.updatePurchaseOrder', error);
            throw error;
        }
    },

    /**
     * Submit a PO (change status from draft to submitted)
     */
    async submitPurchaseOrder(id: string): Promise<PurchaseOrder> {
        return this.updatePurchaseOrder(id, { status: 'submitted' });
    },

    /**
     * Cancel a purchase order
     */
    async cancelPurchaseOrder(id: string): Promise<PurchaseOrder> {
        return this.updatePurchaseOrder(id, { status: 'cancelled' });
    },

    // ============================================
    // LINE ITEMS
    // ============================================

    /**
     * Get line items for a purchase order
     */
    async getLineItems(purchaseOrderId: string): Promise<POLineItem[]> {
        try {
            const { data, error } = await supabase
                .from('po_line_items')
                .select('*')
                .eq('purchase_order_id', purchaseOrderId)
                .order('line_number', { ascending: true });

            if (error) throw error;

            return (data || []).map(mapDbToLineItem);
        } catch (error) {
            logErrorWithTrace('purchaseOrderService.getLineItems', error);
            throw error;
        }
    },

    /**
     * Add a line item to an existing PO
     */
    async addLineItem(purchaseOrderId: string, item: POLineItemInput): Promise<POLineItem> {
        try {
            // Get current max line number
            const { data: existing } = await supabase
                .from('po_line_items')
                .select('line_number')
                .eq('purchase_order_id', purchaseOrderId)
                .order('line_number', { ascending: false })
                .limit(1);

            const nextLineNumber = existing && existing.length > 0 ? existing[0].line_number + 1 : 1;

            const { data, error } = await supabase
                .from('po_line_items')
                .insert({
                    purchase_order_id: purchaseOrderId,
                    product_id: item.productId,
                    line_number: nextLineNumber,
                    quantity_ordered: item.quantityOrdered,
                    unit_cost: item.unitCost,
                    lot_number: item.lotNumber,
                    expiry_date: item.expiryDate,
                })
                .select()
                .single();

            if (error) throw error;

            return mapDbToLineItem(data);
        } catch (error) {
            logErrorWithTrace('purchaseOrderService.addLineItem', error);
            throw error;
        }
    },

    /**
     * Update a line item
     */
    async updateLineItem(
        id: string,
        updates: Partial<Pick<POLineItem, 'quantityOrdered' | 'unitCost' | 'lotNumber' | 'expiryDate'>>
    ): Promise<POLineItem> {
        try {
            const updateData: Record<string, unknown> = {};

            if (updates.quantityOrdered !== undefined) updateData.quantity_ordered = updates.quantityOrdered;
            if (updates.unitCost !== undefined) updateData.unit_cost = updates.unitCost;
            if (updates.lotNumber !== undefined) updateData.lot_number = updates.lotNumber;
            if (updates.expiryDate !== undefined) updateData.expiry_date = updates.expiryDate;

            const { data, error } = await supabase
                .from('po_line_items')
                .update(updateData)
                .eq('id', id)
                .select()
                .single();

            if (error) throw error;

            return mapDbToLineItem(data);
        } catch (error) {
            logErrorWithTrace('purchaseOrderService.updateLineItem', error);
            throw error;
        }
    },

    /**
     * Delete a line item
     */
    async deleteLineItem(id: string): Promise<boolean> {
        try {
            const { error } = await supabase
                .from('po_line_items')
                .delete()
                .eq('id', id);

            if (error) throw error;

            return true;
        } catch (error) {
            logErrorWithTrace('purchaseOrderService.deleteLineItem', error);
            throw error;
        }
    },

    // ============================================
    // RECEIVING
    // ============================================

    /**
     * Record receiving against a purchase order
     */
    async recordReceiving(
        input: ReceivingInput,
        receivedBy: string,
        tenantId: string
    ): Promise<ReceivingLog> {
        try {
            // Get the PO
            const { data: po, error: poError } = await supabase
                .from('purchase_orders')
                .select('*')
                .eq('id', input.purchaseOrderId)
                .single();

            if (poError) throw poError;

            // Calculate totals
            const totalUnitsReceived = input.lineItems.reduce((sum, item) => sum + item.quantityReceived, 0);
            const totalUnitsDamaged = input.lineItems.reduce((sum, item) => sum + (item.quantityDamaged || 0), 0);

            // Create receiving log
            const { data: log, error: logError } = await supabase
                .from('receiving_logs')
                .insert({
                    tenant_id: tenantId,
                    purchase_order_id: input.purchaseOrderId,
                    received_by: receivedBy,
                    receiving_type: input.receivingType,
                    total_units_received: totalUnitsReceived,
                    total_units_damaged: totalUnitsDamaged,
                    notes: input.notes,
                    photo_urls: input.photoUrls || [],
                    quality_check_passed: input.qualityCheckPassed,
                    quality_issues: input.qualityIssues || [],
                })
                .select()
                .single();

            if (logError) throw logError;

            // Create receiving line items and update PO line items
            for (const item of input.lineItems) {
                // Get the PO line item
                const { data: poLine } = await supabase
                    .from('po_line_items')
                    .select('*')
                    .eq('id', item.poLineItemId)
                    .single();

                if (!poLine) continue;

                // Create receiving line item
                await supabase
                    .from('receiving_line_items')
                    .insert({
                        receiving_log_id: log.id,
                        po_line_item_id: item.poLineItemId,
                        product_id: poLine.product_id,
                        quantity_received: item.quantityReceived,
                        quantity_damaged: item.quantityDamaged || 0,
                        condition: item.condition || 'good',
                        condition_notes: item.conditionNotes,
                        storage_location: item.storageLocation,
                    });

                // Update PO line item quantities
                await supabase
                    .from('po_line_items')
                    .update({
                        quantity_received: (poLine.quantity_received || 0) + item.quantityReceived,
                        quantity_damaged: (poLine.quantity_damaged || 0) + (item.quantityDamaged || 0),
                    })
                    .eq('id', item.poLineItemId);
            }

            // Update PO status and actual arrival
            const newStatus: POStatus = input.receivingType === 'full' ? 'received' : 'partially_received';
            await supabase
                .from('purchase_orders')
                .update({
                    status: newStatus,
                    actual_arrival: new Date().toISOString(),
                    received_by: receivedBy,
                })
                .eq('id', input.purchaseOrderId);

            return mapDbToReceivingLog(log);
        } catch (error) {
            logErrorWithTrace('purchaseOrderService.recordReceiving', error);
            throw error;
        }
    },

    /**
     * Get receiving logs for a purchase order
     */
    async getReceivingLogs(purchaseOrderId: string): Promise<ReceivingLog[]> {
        try {
            const { data, error } = await supabase
                .from('receiving_logs')
                .select('*')
                .eq('purchase_order_id', purchaseOrderId)
                .order('received_at', { ascending: false });

            if (error) throw error;

            return (data || []).map(mapDbToReceivingLog);
        } catch (error) {
            logErrorWithTrace('purchaseOrderService.getReceivingLogs', error);
            throw error;
        }
    },

    /**
     * Get receiving line items for a receiving log
     */
    async getReceivingLineItems(receivingLogId: string): Promise<ReceivingLineItem[]> {
        try {
            const { data, error } = await supabase
                .from('receiving_line_items')
                .select('*')
                .eq('receiving_log_id', receivingLogId);

            if (error) throw error;

            return (data || []).map(row => ({
                id: row.id,
                receivingLogId: row.receiving_log_id,
                poLineItemId: row.po_line_item_id,
                productId: row.product_id,
                quantityReceived: row.quantity_received ?? 0,
                quantityDamaged: row.quantity_damaged ?? 0,
                quantityReturned: row.quantity_returned ?? 0,
                condition: row.condition || 'good',
                conditionNotes: row.condition_notes,
                storageLocation: row.storage_location,
                createdAt: row.created_at,
            }));
        } catch (error) {
            logErrorWithTrace('purchaseOrderService.getReceivingLineItems', error);
            throw error;
        }
    },

    // ============================================
    // ANALYTICS & QUERIES
    // ============================================

    /**
     * Get overdue purchase orders
     */
    async getOverduePOs(): Promise<OverduePurchaseOrder[]> {
        try {
            const { data, error } = await supabase
                .from('overdue_purchase_orders')
                .select('*')
                .order('expected_arrival', { ascending: true });

            if (error) throw error;

            return (data || []).map(row => ({
                ...mapDbToPurchaseOrder(row),
                supplierName: row.supplier_name,
                reliabilityGrade: row.reliability_grade,
                hoursOverdue: row.hours_overdue ?? 0,
            }));
        } catch (error) {
            logErrorWithTrace('purchaseOrderService.getOverduePOs', error);
            throw error;
        }
    },

    /**
     * Get POs by status
     */
    async getPOsByStatus(status: POStatus): Promise<PurchaseOrder[]> {
        try {
            const { data, error } = await supabase
                .from('purchase_orders')
                .select('*')
                .eq('status', status)
                .order('order_date', { ascending: false });

            if (error) throw error;

            return (data || []).map(mapDbToPurchaseOrder);
        } catch (error) {
            logErrorWithTrace('purchaseOrderService.getPOsByStatus', error);
            throw error;
        }
    },

    /**
     * Get short-shipped POs
     */
    async getShortShippedPOs(threshold: number = 2): Promise<PurchaseOrder[]> {
        try {
            const { data, error } = await supabase
                .from('purchase_orders')
                .select('*')
                .eq('is_short_shipped', true)
                .gte('short_ship_percentage', threshold)
                .order('short_ship_percentage', { ascending: false });

            if (error) throw error;

            return (data || []).map(mapDbToPurchaseOrder);
        } catch (error) {
            logErrorWithTrace('purchaseOrderService.getShortShippedPOs', error);
            throw error;
        }
    },

    /**
     * Get POs requiring review
     */
    async getPOsRequiringReview(): Promise<PurchaseOrder[]> {
        try {
            const { data, error } = await supabase
                .from('purchase_orders')
                .select('*')
                .eq('requires_review', true)
                .order('updated_at', { ascending: false });

            if (error) throw error;

            return (data || []).map(mapDbToPurchaseOrder);
        } catch (error) {
            logErrorWithTrace('purchaseOrderService.getPOsRequiringReview', error);
            throw error;
        }
    },

    /**
     * Calculate on-time delivery rate for a date range
     */
    async calculateOnTimeDeliveryRate(dateRange: DateRange): Promise<number> {
        try {
            const { data, error } = await supabase
                .from('purchase_orders')
                .select('is_late')
                .in('status', ['received', 'partially_received'])
                .gte('actual_arrival', dateRange.start)
                .lte('actual_arrival', dateRange.end);

            if (error) throw error;

            if (!data || data.length === 0) return 100;

            const onTime = data.filter(po => !po.is_late).length;
            return (onTime / data.length) * 100;
        } catch (error) {
            logErrorWithTrace('purchaseOrderService.calculateOnTimeDeliveryRate', error);
            throw error;
        }
    },

    /**
     * Calculate short-ship rate for a date range
     */
    async calculateShortShipRate(dateRange: DateRange): Promise<number> {
        try {
            const { data, error } = await supabase
                .from('purchase_orders')
                .select('total_units_ordered, total_units_received')
                .in('status', ['received', 'partially_received'])
                .gte('actual_arrival', dateRange.start)
                .lte('actual_arrival', dateRange.end);

            if (error) throw error;

            if (!data || data.length === 0) return 0;

            const totalOrdered = data.reduce((sum, po) => sum + (po.total_units_ordered || 0), 0);
            const totalReceived = data.reduce((sum, po) => sum + (po.total_units_received || 0), 0);

            if (totalOrdered === 0) return 0;
            return ((totalOrdered - totalReceived) / totalOrdered) * 100;
        } catch (error) {
            logErrorWithTrace('purchaseOrderService.calculateShortShipRate', error);
            throw error;
        }
    },

    /**
     * Get PO statistics
     */
    async getPOStats(tenantId: string): Promise<{
        total: number;
        byStatus: Record<POStatus, number>;
        overdue: number;
        shortShipped: number;
        requiresReview: number;
        avgLeadTimeDays: number;
    }> {
        if (!tenantId) {
            console.warn('[purchaseOrderService.getPOStats] tenantId is required');
            return { total: 0, byStatus: { draft: 0, submitted: 0, confirmed: 0, in_transit: 0, partially_received: 0, received: 0, cancelled: 0, disputed: 0 } as Record<POStatus, number>, overdue: 0, shortShipped: 0, requiresReview: 0, avgLeadTimeDays: 0 };
        }
        try {
            const { data: pos, error } = await supabase
                .from('purchase_orders')
                .select('status, is_late, is_short_shipped, requires_review, order_date, actual_arrival')
                .eq('tenant_id', tenantId);

            if (error) throw error;

            const stats = {
                total: pos?.length ?? 0,
                byStatus: {
                    draft: 0,
                    submitted: 0,
                    confirmed: 0,
                    in_transit: 0,
                    partially_received: 0,
                    received: 0,
                    cancelled: 0,
                    disputed: 0,
                } as Record<POStatus, number>,
                overdue: 0,
                shortShipped: 0,
                requiresReview: 0,
                avgLeadTimeDays: 0,
            };

            let totalLeadTime = 0;
            let countWithLeadTime = 0;

            (pos || []).forEach(po => {
                if (po.status) {
                    stats.byStatus[po.status as POStatus]++;
                }
                if (po.is_late) stats.overdue++;
                if (po.is_short_shipped) stats.shortShipped++;
                if (po.requires_review) stats.requiresReview++;

                // Calculate lead time for completed POs
                if (po.actual_arrival && po.order_date) {
                    const orderDate = new Date(po.order_date);
                    const arrivalDate = new Date(po.actual_arrival);
                    const leadTime = (arrivalDate.getTime() - orderDate.getTime()) / (1000 * 60 * 60 * 24);
                    totalLeadTime += leadTime;
                    countWithLeadTime++;
                }
            });

            stats.avgLeadTimeDays = countWithLeadTime > 0 ? totalLeadTime / countWithLeadTime : 0;

            return stats;
        } catch (error) {
            logErrorWithTrace('purchaseOrderService.getPOStats', error);
            throw error;
        }
    },

    /**
     * Get receiving variance data for a store.
     * Joins receiving_logs → purchase_orders → po_line_items.
     * Uses LP fields: receipt_number, receiving_type (asn_number is not deployed).
     * SECURITY: tenantId required for tenant isolation.
     */
    async getReceivingVariance(
        tenantId: string,
        storeId: string,
    ): Promise<Array<{
        poNumber: string;
        asnNumber: string | null;
        receiptNumber: string | null;
        receivingMethod: string | null;
        expected: number;
        received: number;
        variance: number;
        receivedAt: string | null;
    }>> {
        if (!tenantId) return [];

        try {
            const { data, error } = await supabase
                .from('purchase_orders')
                .select(`
                    po_number,
                    total_units_ordered,
                    total_units_received,
                    actual_arrival,
                    receiving_logs (
                        receipt_number,
                        receiving_method:receiving_type,
                        received_at
                    )
                `)
                .eq('tenant_id', tenantId)
                .eq('store_id', storeId)
                .in('status', ['received', 'partially_received'])
                .order('actual_arrival', { ascending: false })
                .limit(20);

            if (error) {
                console.error('[purchaseOrderService.getReceivingVariance] Error:', error);
                return [];
            }

            return (data || []).map((po: any) => {
                const expected = po.total_units_ordered ?? 0;
                const received = po.total_units_received ?? 0;
                const log = Array.isArray(po.receiving_logs) && po.receiving_logs.length > 0
                    ? po.receiving_logs[0]
                    : null;
                return {
                    poNumber: po.po_number || '',
                    asnNumber: null, // purchase_orders.asn_number is not deployed
                    receiptNumber: log?.receipt_number ?? null,
                    receivingMethod: log?.receiving_method ?? null,
                    expected,
                    received,
                    variance: expected - received,
                    receivedAt: log?.received_at ?? po.actual_arrival ?? null,
                };
            });
        } catch (error) {
            logErrorWithTrace('purchaseOrderService.getReceivingVariance', error);
            return [];
        }
    },
};

export default purchaseOrderService;
